/**
 * 1.8.4 §3 (D-1.8.4-3) — "Call me" in My settings: the name Today's greeting uses ("Good morning, Eli."). A short
 * labelled text box and Save; kept on the server per person (PUT /api/me/settings { callMe }), so every device greets
 * them the same. Empty: their first name. The outcome is said in the live region.
 */
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MeResponse } from '@shared/admin';
import { CALL_ME_MAX, cleanCallMe, type MySettings } from '@shared/my-settings';
import { send, useMeExtras } from '@/lib/admin';
import { ApiRequestError } from '@/lib/api';
import { useAnnounce } from '@/components/Announcer';
import { Button } from '@/components/ui/button';
import '@/styles/today184.css';

export function CallMeField() {
  const { t } = useTranslation();
  const id = useId();
  const me = useMeExtras();
  const qc = useQueryClient();
  const announce = useAnnounce();
  const saved = me.data?.settings?.callMe ?? '';
  const [value, setValue] = useState(saved);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);

  // the account's answer arrives after the page: show it until the person types
  useEffect(() => {
    if (!touched) setValue(saved);
  }, [saved, touched]);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const clean = cleanCallMe(value);
    if (clean === undefined) {
      const msg = t('today184.callMe.failed', { why: t('today184.callMe.help') });
      setOutcome(msg);
      announce(msg, 'assertive');
      return;
    }
    setBusy(true);
    try {
      const out = await send<MySettings>('/api/me/settings', 'PUT', { callMe: clean });
      qc.setQueryData<MeResponse>(['me-extras'], (d) => (d ? { ...d, settings: out } : d));
      setValue(clean ?? '');
      setTouched(false);
      const msg = clean ? t('today184.callMe.saved', { name: clean }) : t('today184.callMe.cleared');
      setOutcome(msg);
      announce(msg);
    } catch (err) {
      const msg = t('today184.callMe.failed', { why: err instanceof ApiRequestError ? err.message : String(err) });
      setOutcome(msg);
      announce(msg, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="t184-callme" onSubmit={(e) => void save(e)} data-testid="callme-form">
      <label htmlFor={`${id}-callme`}>{t('today184.callMe.label')}</label>
      <p className="help" id={`${id}-callme-help`}>
        {t('today184.callMe.help')}
      </p>
      <div className="t184-callme-row">
        <input
          id={`${id}-callme`}
          className="input"
          type="text"
          autoComplete="nickname"
          maxLength={CALL_ME_MAX}
          value={value}
          aria-describedby={`${id}-callme-help`}
          onChange={(e) => {
            setTouched(true);
            setValue(e.currentTarget.value);
          }}
          data-testid="callme-input"
        />
        <Button type="submit" busy={busy} size="sm" disabled={busy} data-testid="callme-save">
          {t('today184.callMe.save')}
        </Button>
      </div>
      {outcome ? (
        <p className="help" data-testid="callme-outcome">
          {outcome}
        </p>
      ) : null}
    </form>
  );
}
